'use client'

import { useEffect, useState } from "react"
import { ArrowRight, LogIn, ShieldCheck, Trash2 } from "lucide-react"

import { Button } from "@/components/elements/Button"
import Select from "@/components/elements/Select"
import { useDomainCart, periodItems, CART_LOGIN_HREF, checkoutBlocker } from "@/hooks/use-domain-cart"
import { sendRequest } from "@/lib/request"
import { formatPrice } from "@/lib/util"

/**
 * The basket, inline beneath the search field.
 *
 * Collecting names happens here, on the page the buyer is already looking at.
 * Committing to them does not: WHOIS contacts, the payment rail and the price
 * breakdown need room and a URL of their own, so the pay button leads to
 * `/checkout` rather than trying to be a checkout itself.
 */
export default function CartPanel({ signedIn, danVerified = false, price }: { signedIn: boolean, danVerified?: boolean, price?: number | null }) {
  const { items, loaded, remove, setPeriod } = useDomainCart()

  const [validated, setValidated] = useState<any[] | null>(null)
  const [checking, setChecking] = useState(false)

  const key = items.map(i => `${i.domain}:${i.period}`).join(',')

  // The basket is browser-local, so its contents can be days old: a name may be
  // gone and the price may have moved. Signed-out visitors see the indicative
  // price instead — the endpoint is owner-scoped, and checkout re-validates.
  useEffect(() => {
    if (!signedIn || !loaded || items.length === 0) { setValidated(null); return }
    let cancelled = false
    setChecking(true)
    sendRequest({ url: '/manage/domain/cart/validate', method: 'POST', body: { items } })
      .then((res: any) => { if (!cancelled && res?.data?.items) setValidated(res.data.items) })
      .finally(() => { if (!cancelled) setChecking(false) })
    return () => { cancelled = true }
  }, [key, signedIn, loaded])

  if (!loaded) return null
  if (items.length === 0) return null

  // What the pay button has to become, if anything. Same helper the search
  // result's buy button uses, so the two never disagree about what is missing.
  const blocker = checkoutBlocker({ signedIn, danVerified })

  const lineFor = (domain: string) => validated?.find(v => v.domain === domain)
  const lineTotal = (domain: string, period: number) => {
    const v = lineFor(domain)
    if (typeof v?.totalAmount === 'number') return v.totalAmount
    return price ? price * period : null
  }

  const unavailable = items.filter(i => lineFor(i.domain)?.available === false)
  const total = items.reduce((sum, i) => {
    if (lineFor(i.domain)?.available === false) return sum
    return sum + (lineTotal(i.domain, i.period) || 0)
  }, 0)

  return (
    <div className="mt-8">
      <p className="text-sm text-muted-foreground mb-3">
        Сагс · {items.length} домэйн нэр
      </p>

      <div className="flex flex-col gap-2">
        { items.map(item => {
          const v = lineFor(item.domain)
          const gone = v?.available === false
          const line = lineTotal(item.domain, item.period)
          return (
            <div key={item.domain} className={`main-card p-3 flex items-center gap-3 ${gone ? 'opacity-60' : ''}`}>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm break-all">{item.domain}</p>
                <p className={`text-xs mt-0.5 ${gone ? 'text-warning' : 'text-muted-foreground'}`}>
                  { gone ? (v?.reason || 'Боломжгүй болсон') : (line !== null ? formatPrice(line) : '—') }
                </p>
              </div>

              { periodItems(item.allowedYears).length > 1 ? (
              <div className="w-24 shrink-0">
                <Select
                  value={String(item.period)}
                  onChange={(val) => setPeriod(item.domain, parseInt(val))}
                  items={periodItems(item.allowedYears)}
                />
              </div>
              ) : null }

              <Button
                tag="button"
                variant="outline"
                size="icon"
                aria-label={`${item.domain} хасах`}
                className="shrink-0 !text-danger"
                onClick={() => remove(item.domain)}
              >
                <Trash2 className="size-4" aria-hidden="true" />
              </Button>
            </div>
          )
        }) }
      </div>

      <div className="main-card p-3 sm:p-4 mt-3 flex items-center justify-between gap-4">
        <div>
          <p className="text-xs text-muted-foreground">Нийт</p>
          <p className="font-gilroy font-semibold text-lg tracking-tight">
            { checking ? '…' : formatPrice(total) }
          </p>
        </div>

        {/* The basket button names whatever actually stands between this basket
            and a payment, rather than always saying "pay" and failing. Both
            detours return to this page with the basket intact - it lives in
            localStorage, so nothing here is lost on the way. */}
        { blocker === CART_LOGIN_HREF ? (
          <Button tag="link" href={blocker}>
            <LogIn className="size-5" aria-hidden="true" />
            Нэвтрэх
          </Button>
        ) : blocker ? (
          <Button tag="link" href={blocker}>
            <ShieldCheck className="size-5" aria-hidden="true" />
            ДАН-аар баталгаажуулах
          </Button>
        ) : (
          // A link, not a submit: /checkout owns the commitment step, and an
          // unavailable line is disabled there rather than refused here.
          <Button tag="link" href="/checkout" isActive={!checking && unavailable.length === 0}>
            Үргэлжлүүлэх
            <ArrowRight className="size-5" aria-hidden="true" />
          </Button>
        ) }
      </div>
    </div>
  )
}
